import { useState } from "react";
import { ArrowDown, ArrowUp, ImagePlus, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText, photoUrl } from "../../lib/api";
import { INFO_ICONS } from "../../lib/infoBoxes";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";

export const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs font-semibold text-slate-500">{label}</span><div className="mt-1">{children}</div></label>
);

export const ImagePick = ({ label, value, onChange, testid, round }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <div className={`${round ? "h-12 w-12" : "h-12 w-20"} rounded-lg border border-dashed border-slate-300 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0`}>
        {value ? <img src={photoUrl(value)} alt="" className={`w-full h-full ${round ? "object-contain p-1" : "object-cover"}`} /> : <ImagePlus size={16} className="text-slate-300" />}
      </div>
      <div className="flex flex-col gap-1">
        <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#F5551E] cursor-pointer hover:underline" data-testid={`${testid}-upload`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} {value ? "Replace" : "Upload"} {label}
          <input type="file" accept="image/*" className="hidden" onChange={pick} />
        </label>
        {value && <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 w-fit" data-testid={`${testid}-remove`}><X size={12} /> Remove</button>}
      </div>
    </div>
  );
};

export const InfoBoxEditor = ({ b, i, total, hidden, onChange, onMove, onRemove }) => {
  const set = (k) => (e) => onChange({ ...b, [k]: typeof e === "string" ? e : e.target.value });
  const t = (k) => `info-box-${i}-${k}`;
  return (
    <div className={`rounded-2xl border bg-white p-5 ${hidden ? "border-dashed border-slate-300 opacity-70" : "border-slate-200"}`} data-testid={`info-box-editor-${i}`}>
      <div className="flex items-center justify-between mb-4">
        <p className="font-bold text-[#12234A]">Box {i + 1} {hidden && <span className="ml-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Hidden — over the shown number</span>}</p>
        <div className="flex items-center gap-1">
          <button type="button" disabled={i === 0} onClick={() => onMove(-1)} className="h-8 w-8 rounded-lg border flex items-center justify-center disabled:opacity-30" data-testid={t("up")} aria-label="Move up"><ArrowUp size={14} /></button>
          <button type="button" disabled={i === total - 1} onClick={() => onMove(1)} className="h-8 w-8 rounded-lg border flex items-center justify-center disabled:opacity-30" data-testid={t("down")} aria-label="Move down"><ArrowDown size={14} /></button>
          <button type="button" onClick={onRemove} className="h-8 w-8 rounded-lg border flex items-center justify-center text-red-500 hover:bg-red-50" data-testid={t("remove")} aria-label="Remove box"><Trash2 size={14} /></button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Title (English)"><Input value={b.title_en} onChange={set("title_en")} maxLength={60} data-testid={t("title-en")} /></Field>
        <Field label="Title (French)"><Input value={b.title_fr} onChange={set("title_fr")} maxLength={60} data-testid={t("title-fr")} /></Field>
        <Field label="Text (English)"><Textarea rows={3} value={b.body_en} onChange={set("body_en")} maxLength={240} data-testid={t("body-en")} /></Field>
        <Field label="Text (French)"><Textarea rows={3} value={b.body_fr} onChange={set("body_fr")} maxLength={240} data-testid={t("body-fr")} /></Field>
        <Field label="Button text (English) — optional"><Input value={b.button_en} onChange={set("button_en")} maxLength={30} placeholder="e.g. Learn more" data-testid={t("btn-en")} /></Field>
        <Field label="Button text (French) — optional"><Input value={b.button_fr} onChange={set("button_fr")} maxLength={30} placeholder="ex. En savoir plus" data-testid={t("btn-fr")} /></Field>
        <div className="md:col-span-2"><Field label="Button link (a page like /get-verified, or https://…)"><Input value={b.button_link} onChange={set("button_link")} placeholder="/get-verified" data-testid={t("btn-link")} /></Field></div>
      </div>
      <div className="mt-5 grid md:grid-cols-2 gap-5">
        <div>
          <p className="text-xs font-semibold text-slate-500 mb-2">Icon — default</p>
          <div className="flex flex-wrap gap-1.5" data-testid={t("icons")}>
            {Object.entries(INFO_ICONS).map(([k, v]) => (
              <button key={k} type="button" onClick={() => set("icon")(k)} title={v.label} data-testid={`${t("icon")}-${k}`}
                className={`h-9 w-9 rounded-lg flex items-center justify-center transition-colors ${b.icon === k ? "bg-[#12234A] text-[#F5551E]" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                <v.icon size={17} />
              </button>
            ))}
          </div>
          <div className="mt-3"><ImagePick label="own icon" value={b.icon_image} onChange={set("icon_image")} testid={t("icon-img")} round /></div>
          <p className="text-[11px] text-slate-400 mt-1">Your own icon replaces the default one. Remove it to go back.</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-500 mb-2">Background image — optional</p>
          <ImagePick label="background" value={b.bg_image} onChange={set("bg_image")} testid={t("bg")} />
          <p className="text-[11px] text-slate-400 mt-1">Text turns white over a dark tint so it stays readable.</p>
        </div>
      </div>
    </div>
  );
};
